import { useState, useEffect, useCallback, useRef } from "react";
import { usePermissions } from "./PermissionsContext";
import { createPortal } from "react-dom";
import "./index.css";

// ─── (All helpers, API, map components, form components are unchanged) ────────

const API_BASE = "https://api.seritonex.com";

const SA_PROVINCES = [
  { value: "", label: "— Select Province —" },
  { value: "Eastern Cape", label: "Eastern Cape" },
  { value: "Free State", label: "Free State" },
  { value: "Gauteng", label: "Gauteng" },
  { value: "KwaZulu-Natal", label: "KwaZulu-Natal" },
  { value: "Limpopo", label: "Limpopo" },
  { value: "Mpumalanga", label: "Mpumalanga" },
  { value: "North West", label: "North West" },
  { value: "Northern Cape", label: "Northern Cape" },
  { value: "Western Cape", label: "Western Cape" },
];

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    return null;
  }
}

function toArray(data, ...keys) {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object") return [val];
  }
  if (data && typeof data === "object" && !Array.isArray(data)) return [data];
  return [];
}

function resolveUserId(userOrId) {
  if (userOrId == null) return null;
  if (typeof userOrId === "number" && !isNaN(userOrId)) return userOrId;
  if (typeof userOrId === "string") {
    const n = Number(userOrId);
    if (!isNaN(n) && n > 0) return n;
  }
  if (typeof userOrId === "object") {
    const raw = userOrId._rawUserId ?? userOrId.user_id ?? userOrId.id;
    if (raw != null && !isNaN(Number(raw)) && Number(raw) > 0)
      return Number(raw);
  }
  return null;
}

let leafletPromise = null;
function loadLeaflet() {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
  return leafletPromise;
}

async function geocodeAddress(query) {
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "en", "User-Agent": "AMIS-App/1.0" },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((item) => ({
      display: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      addr1: [item.address?.house_number, item.address?.road]
        .filter(Boolean)
        .join(" "),
      addr2: item.address?.suburb || item.address?.neighbourhood || "",
      city:
        item.address?.city ||
        item.address?.town ||
        item.address?.village ||
        item.address?.municipality ||
        "",
      region:
        item.address?.state ||
        item.address?.province ||
        item.address?.region ||
        "",
      postal: item.address?.postcode || "",
      country: item.address?.country || "",
    }));
  } catch {
    return [];
  }
}

async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "en", "User-Agent": "AMIS-App/1.0" },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function LeafletMap({
  center,
  zoom = 13,
  onClickPin,
  markerPos,
  interactive = true,
  draggableMarker = false,
}) {
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const containerId = useRef(`amp-map-${Math.random().toString(36).slice(2)}`);
  const onClickPinRef = useRef(onClickPin);
  useEffect(() => {
    onClickPinRef.current = onClickPin;
  }, [onClickPin]);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || mapInstanceRef.current) return;
      const map = L.map(containerId.current, {
        center: center || [-29.0, 25.0],
        zoom,
        zoomControl: true,
        attributionControl: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);
      if (interactive || draggableMarker) {
        map.on("click", (e) =>
          onClickPinRef.current?.(e.latlng.lat, e.latlng.lng),
        );
        map.getContainer().style.cursor = "crosshair";
      }
      mapInstanceRef.current = map;
    });
    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    loadLeaflet().then((L) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      if (markerPos) {
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:18px;height:18px;background:#e24b4a;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 18],
        });
        if (markerRef.current) {
          markerRef.current.setLatLng([markerPos.lat, markerPos.lng]);
          if (draggableMarker) markerRef.current.dragging?.enable();
          else markerRef.current.dragging?.disable();
        } else {
          const m = L.marker([markerPos.lat, markerPos.lng], {
            icon,
            draggable: draggableMarker,
          }).addTo(map);
          if (draggableMarker) {
            m.on("dragend", () => {
              const ll = m.getLatLng();
              const rLat = Math.round(ll.lat * 1e6) / 1e6,
                rLng = Math.round(ll.lng * 1e6) / 1e6;
              map.getContainer().style.cursor = "crosshair";
              onClickPinRef.current?.(rLat, rLng);
            });
          }
          markerRef.current = m;
        }
        map.setView(
          [markerPos.lat, markerPos.lng],
          Math.max(map.getZoom(), 14),
        );
      } else if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    });
  }, [markerPos, draggableMarker]);

  return (
    <div
      id={containerId.current}
      style={{
        height: 400,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid var(--border)", // ← was rgba(0,0,0,0.15)
        marginBottom: 12,
      }}
    />
  );
}

function AddressMapPicker({ value = {}, onChange, required = false }) {
  const [mode, setMode] = useState(value.mode || "search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [loc, setLoc] = useState({
    confirmed: false,
    lat: null,
    lng: null,
    addr1: "",
    addr2: "",
    city: "",
    region: "",
    postal: "",
    country: "",
    label: "",
    unitNumber: "",
    ...value,
  });
  const [pinAdjusted, setPinAdjusted] = useState(false);

  const emit = useCallback(
    (update) => {
      const next = { ...loc, ...update, mode };
      setLoc(next);
      onChange?.(next);
    },
    [loc, mode, onChange],
  );

  function switchMode(m) {
    setMode(m);
    setSearchResults([]);
    setSearchError("");
    setPinAdjusted(false);
    const reset = {
      confirmed: false,
      lat: null,
      lng: null,
      addr1: "",
      addr2: "",
      city: "",
      region: "",
      postal: "",
      country: "",
      label: "",
      unitNumber: "",
      mode: m,
    };
    setLoc(reset);
    onChange?.(reset);
  }

  async function doSearch() {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError("");
    setSearchResults([]);
    const results = await geocodeAddress(searchQuery);
    if (results.length === 0)
      setSearchError(
        "No results found. Try a more specific address or use Drop Pin mode.",
      );
    setSearchResults(results);
    setSearching(false);
  }

  function selectResult(r) {
    setSearchResults([]);
    setSearchQuery(r.display);
    setPinAdjusted(false);
    const matchedProvince =
      SA_PROVINCES.find(
        (p) =>
          p.value && r.region?.toLowerCase().includes(p.value.toLowerCase()),
      )?.value || r.region;
    emit({
      confirmed: true,
      lat: r.lat,
      lng: r.lng,
      addr1: r.addr1,
      addr2: r.addr2,
      city: r.city,
      region: matchedProvince,
      postal: r.postal,
      country: r.country,
    });
  }

  function clearSelection() {
    setSearchQuery("");
    setSearchResults([]);
    setPinAdjusted(false);
    emit({
      confirmed: false,
      lat: null,
      lng: null,
      addr1: "",
      addr2: "",
      city: "",
      region: "",
      postal: "",
      country: "",
      unitNumber: "",
    });
  }

  async function handleMapClick(lat, lng) {
    const rLat = Math.round(lat * 1e6) / 1e6,
      rLng = Math.round(lng * 1e6) / 1e6;
    if (mode === "search") {
      setPinAdjusted(true);
      emit({ confirmed: true, lat: rLat, lng: rLng });
    } else {
      const rev = await reverseGeocode(rLat, rLng);
      const autoLabel = rev?.display_name
        ? rev.display_name.split(",").slice(0, 3).join(", ")
        : `${rLat}, ${rLng}`;
      emit({
        confirmed: true,
        lat: rLat,
        lng: rLng,
        label: loc.label || autoLabel,
      });
    }
  }

  function editField(key, val) {
    const next = { ...loc, [key]: val, confirmed: false, mode };
    setLoc(next);
    onChange?.(next);
  }

  const isLocked = loc.confirmed && mode === "search";

  // ── all colours now use CSS custom properties so they theme-switch correctly ──
  const inputStyle = (locked) => ({
    width: "100%",
    height: 34,
    padding: "0 10px",
    border: `1px ${locked ? "dashed" : "solid"} var(--border)`,
    borderRadius: 8,
    fontSize: 13,
    background: locked ? "var(--elevated)" : "var(--deep)",
    color: locked ? "var(--text3)" : "var(--text1)",
    outline: "none",
    cursor: locked ? "not-allowed" : "text",
    boxSizing: "border-box",
  });

  const selectStyle = (locked) => ({
    width: "100%",
    height: 34,
    padding: "0 10px",
    border: `1px ${locked ? "dashed" : "solid"} var(--border)`,
    borderRadius: 8,
    fontSize: 13,
    background: locked ? "var(--elevated)" : "var(--deep)",
    color: locked ? "var(--text3)" : "var(--text1)",
    outline: "none",
    cursor: locked ? "not-allowed" : "default",
    boxSizing: "border-box",
    appearance: "auto",
  });

  // reusable inline pill styles
  const pillSuccess = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 8px",
    borderRadius: 6,
    background: "rgba(0,230,118,.08)",
    color: "var(--success)",
    border: "1px solid rgba(0,230,118,.2)",
  };
  const pillAmber = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 8px",
    borderRadius: 6,
    background: "rgba(255,171,0,.08)",
    color: "var(--amber)",
    border: "1px solid rgba(255,171,0,.2)",
  };
  const ghostBtn = {
    padding: "2px 8px",
    fontSize: 11,
    cursor: "pointer",
    border: "1px solid var(--border)",
    borderRadius: 6,
    background: "transparent",
    color: "var(--text3)",
  };
  const coordBar = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 10px",
    background: "var(--deep)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: 11,
    color: "var(--text2)",
    marginBottom: 12,
  };

  return (
    <div>
      {/* ── mode toggle ─────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          border: "1px solid var(--border)",
          borderRadius: 8,
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        {["search", "pin"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => switchMode(m)}
            style={{
              flex: 1,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: mode === m ? 600 : 500,
              cursor: "pointer",
              border: "none",
              borderRight: m === "search" ? "1px solid var(--border)" : "none",
              background: mode === m ? "var(--elevated)" : "var(--deep)",
              color: mode === m ? "var(--text1)" : "var(--text3)",
              transition: "background .14s, color .14s",
            }}
          >
            {m === "search" ? "🔍 Search address" : "📍 Drop pin on map"}
          </button>
        ))}
      </div>

      {/* ── SEARCH MODE ─────────────────────────────────────────── */}
      {mode === "search" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              style={{ ...inputStyle(isLocked), flex: 1, width: "auto" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Type an address, landmark or area…"
              disabled={isLocked}
            />
            <button
              type="button"
              onClick={isLocked ? clearSelection : doSearch}
              disabled={searching}
              style={{
                height: 34,
                padding: "0 14px",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                background: "var(--elevated)",
                color: "var(--text1)",
                whiteSpace: "nowrap",
                transition: "border-color .14s",
              }}
            >
              {isLocked ? "Clear" : searching ? "Searching…" : "Search"}
            </button>
          </div>

          {searchError && (
            <div
              style={{
                padding: "6px 10px",
                marginBottom: 8,
                borderRadius: 6,
                background: "rgba(255,23,68,.08)",
                border: "1px solid rgba(255,23,68,.25)",
                color: "var(--danger)",
                fontSize: 11,
              }}
            >
              {searchError}
            </div>
          )}

          {searchResults.length > 0 && (
            <div
              style={{
                border: "1px solid var(--border)",
                borderRadius: 8,
                overflow: "hidden",
                marginBottom: 10,
                background: "var(--elevated)",
              }}
            >
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  onClick={() => selectResult(r)}
                  style={{
                    padding: "9px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                    borderBottom:
                      i < searchResults.length - 1
                        ? "1px solid var(--border)"
                        : "none",
                    lineHeight: 1.4,
                    color: "var(--text1)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--hover)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  {r.display}
                </div>
              ))}
            </div>
          )}

          {loc.confirmed && loc.lat != null && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 10px",
                  marginBottom: 6,
                  borderRadius: 6,
                  background: "rgba(255,171,0,.08)",
                  border: "1px solid rgba(255,171,0,.2)",
                  fontSize: 11,
                  color: "var(--amber)",
                  lineHeight: 1.4,
                }}
              >
                <span style={{ fontSize: 13 }}>📍</span>
                <span>
                  <strong>Drag the pin</strong> or{" "}
                  <strong>click anywhere on the map</strong> to fine-tune the
                  exact location.
                </span>
              </div>

              <LeafletMap
                center={[loc.lat, loc.lng]}
                markerPos={{ lat: loc.lat, lng: loc.lng }}
                interactive
                draggableMarker
                onClickPin={handleMapClick}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <span style={pillSuccess}>✓ Location confirmed</span>
                <button type="button" onClick={clearSelection} style={ghostBtn}>
                  Change
                </button>
              </div>

              <div style={coordBar}>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 10,
                    color: "var(--text3)",
                  }}
                >
                  LAT
                </span>
                {loc.lat}
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 10,
                    color: "var(--text3)",
                    marginLeft: 8,
                  }}
                >
                  LNG
                </span>
                {loc.lng}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    color: "var(--text3)",
                  }}
                >
                  {pinAdjusted ? "📍 manually adjusted" : "from geocode"}
                </span>
              </div>
            </>
          )}

          {/* Unit number */}
          <div style={{ marginBottom: 10 }}>
            <div className="fl">
              Unit Number{" "}
              <span style={{ color: "var(--danger)", marginLeft: 2 }}>*</span>
            </div>
            <input
              style={{ ...inputStyle(false), width: "100%" }}
              value={loc.unitNumber || ""}
              onChange={(e) => {
                const next = { ...loc, unitNumber: e.target.value, mode };
                setLoc(next);
                onChange?.(next);
              }}
              placeholder="e.g. Unit 4B, Flat 12, Suite 300"
            />
          </div>

          {/* Address fields */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div className="fl">Address line 1</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.addr1}
                onChange={(e) => editField("addr1", e.target.value)}
                disabled={isLocked}
                placeholder="123 Main St"
              />
            </div>
            <div>
              <div className="fl">City</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.city}
                onChange={(e) => editField("city", e.target.value)}
                disabled={isLocked}
                placeholder="City"
              />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div className="fl">Address line 2</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.addr2}
                onChange={(e) => editField("addr2", e.target.value)}
                disabled={isLocked}
                placeholder="Suite / Unit"
              />
            </div>
            <div>
              <div className="fl">Province</div>
              <select
                style={selectStyle(isLocked)}
                value={loc.region}
                onChange={(e) => editField("region", e.target.value)}
                disabled={isLocked}
              >
                {SA_PROVINCES.map((p) => (
                  <option
                    key={p.value}
                    value={p.value}
                    style={{ background: "var(--deep)", color: "var(--text1)" }}
                  >
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div className="fl">Postal code</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.postal}
                onChange={(e) => editField("postal", e.target.value)}
                disabled={isLocked}
                placeholder="1234"
              />
            </div>
            <div>
              <div className="fl">Country</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.country}
                onChange={(e) => editField("country", e.target.value)}
                disabled={isLocked}
                placeholder="South Africa"
              />
            </div>
          </div>

          {!loc.confirmed && (
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              ℹ️ You can type address fields manually, but searching and
              confirming on the map is recommended for accuracy.
            </div>
          )}
        </>
      )}

      {/* ── PIN MODE ────────────────────────────────────────────── */}
      {mode === "pin" && (
        <>
          {!loc.confirmed ? (
            <div style={{ ...pillAmber, marginBottom: 10 }}>
              Click anywhere on the map to drop your pin
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <span style={pillSuccess}>✓ Pin placed</span>
              <button
                type="button"
                onClick={() =>
                  emit({ confirmed: false, lat: null, lng: null, label: "" })
                }
                style={ghostBtn}
              >
                Repin
              </button>
            </div>
          )}

          <LeafletMap
            center={loc.lat ? [loc.lat, loc.lng] : [-29.0, 25.0]}
            zoom={loc.lat ? 14 : 5}
            markerPos={loc.lat ? { lat: loc.lat, lng: loc.lng } : null}
            onClickPin={handleMapClick}
            interactive
            draggableMarker={loc.confirmed}
          />

          {loc.lat != null && (
            <div style={coordBar}>
              <span
                style={{ fontWeight: 600, fontSize: 10, color: "var(--text3)" }}
              >
                lat
              </span>
              {loc.lat}
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 10,
                  color: "var(--text3)",
                  marginLeft: 8,
                }}
              >
                lng
              </span>
              {loc.lng}
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 10,
                  color: "var(--text3)",
                }}
              >
                drag pin or click map to reposition
              </span>
            </div>
          )}

          {/* Location label */}
          <div style={{ marginBottom: 10 }}>
            <div className="fl">
              Location label{" "}
              {required && (
                <span style={{ color: "var(--danger)", marginLeft: 2 }}>*</span>
              )}
            </div>
            <input
              style={{ ...inputStyle(false), width: "100%" }}
              value={loc.label}
              onChange={(e) => {
                const next = { ...loc, label: e.target.value, mode };
                setLoc(next);
                onChange?.(next);
              }}
              placeholder="e.g. Kruger Park North Gate, Mine Shaft 4, Farm Storage A…"
            />
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              ℹ️ No street address needed. Pin coordinates will be stored. Add a
              descriptive label so staff know what this location is.
            </div>
          </div>

          {/* Unit number */}
          <div style={{ marginBottom: 10 }}>
            <div className="fl">
              Unit Number{" "}
              <span style={{ color: "var(--danger)", marginLeft: 2 }}>*</span>
            </div>
            <input
              style={{ ...inputStyle(false), width: "100%" }}
              value={loc.unitNumber || ""}
              onChange={(e) => {
                const next = { ...loc, unitNumber: e.target.value, mode };
                setLoc(next);
                onChange?.(next);
              }}
              placeholder="e.g. Unit 4B, Flat 12, Suite 300"
            />
          </div>
        </>
      )}
    </div>
  );
}
/* ═══════════════════════════════════════════════════════
   BRANCH API
═══════════════════════════════════════════════════════ */
const API = {
  getBranches: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/branch/get-all");
    if (!data) return null;
    const arr = toArray(data, "branches", "data", "items");
    return arr.map((b) => ({
      id: b.branch_id,
      name: b.branch_name || "",
      code: b.branch_code || b.address?.address_code || "",
      address_code: b.address?.address_code || "",
      region: b.region || b.address?.region || "",
      organization_name:
        b.organization?.name || b.Organization || b.organization || "",
      organization:
        b.organization?.name || b.Organization || b.organization || "",
      status: b.is_active ? "Active" : "Inactive",
      vat: b.vat_registration || b.vat_registration_number || "",
      company: b.company_registration || b.company_registration_number || "",
      addr1: b.address?.address_line_1 || "",
      addr2: b.address?.address_line_2 || "",
      city: b.address?.city || "",
      postal: b.address?.postal_code || "",
      country: b.address?.country || "",
      organization_id: b.organization?.organization_id || null,
      _raw: b,
    }));
  },
  getBranch: (id) =>
    apiFetch("POST", "/api/v1/amis/branch/by-id", { branch_id: id }),
  createBranch: (d) => apiFetch("POST", "/api/v1/amis/branch/create", d),
  updateBranch: (id, d) =>
    apiFetch("POST", "/api/v1/amis/branch/update", { branch_id: id, ...d }),
  deleteBranch: (id) =>
    apiFetch("POST", "/api/v1/amis/branch/delete", { branch_id: id }),
  bindBranch: (branchId, userId) => {
    const numUserId = resolveUserId(userId);
    return apiFetch("POST", "/api/v1/amis/branch/bind", {
      branch_id: branchId,
      user_id: numUserId,
    });
  },
  unbindBranch: (branchId, userId) => {
    const numUserId = resolveUserId(userId);
    return apiFetch("POST", "/api/v1/amis/branch/unbind", {
      branch_id: branchId,
      user_id: numUserId,
    });
  },
  bindCustomerBranch: (supplierBranchId, customerBranchId) =>
    apiFetch("POST", "/api/v1/amis/branch/bind-customer-branch", {
      supplier_branch_id: supplierBranchId,
      customer_branch_id: customerBranchId,
    }),
  unbindCustomerBranch: (supplierBranchId, customerBranchId) =>
    apiFetch("POST", "/api/v1/amis/branch/unbind-customer-branch", {
      supplier_branch_id: supplierBranchId,
      customer_branch_id: customerBranchId,
    }),
  getUnboundCustomerBranches: (branchId) =>
    apiFetch("POST", "/api/v1/amis/branch/unbound-customer-branches", {
      branch_id: branchId,
    }),
  getCustomerBranches: (branch_id) =>
    apiFetch("POST", "/api/v1/amis/branch/branch-customers", { branch_id }),
  getSupplierBranches: (branch_id) =>
    apiFetch("POST", "/api/v1/amis/branch/branch-suppliers", { branch_id }),
  getBranchesByUser: (user_id) => {
    const numId = resolveUserId(user_id);
    return apiFetch("POST", "/api/v1/amis/branch/by-user-id", {
      user_id: numId,
    });
  },
  getUsers: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/user/all");
    if (!Array.isArray(data)) return null;
    return data.map((u) => {
      const rawId = u.user_id ?? u.id ?? null;
      return {
        id: rawId,
        _rawUserId: rawId,
        name: [u.name, u.surname].filter(Boolean).join(" ") || u.fullname || "",
        email: u.email_address || u.email || "",
      };
    });
  },
};

/* ═══════════════════════════════════════════════════════
   PRIMITIVES
═══════════════════════════════════════════════════════ */
function Badge({ children, color = "green" }) {
  return <span className={`badge badge-${color}`}>{children}</span>;
}
function Btn({
  children,
  variant = "p",
  sm,
  onClick,
  disabled,
  type = "button",
  fullWidth,
}) {
  return (
    <button
      type={type}
      className={`btn btn-${variant}${sm ? " btn-sm" : ""}${fullWidth ? " btn-full" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
function FG({ label, children, hint }) {
  return (
    <div className="fg">
      <div className="fl">{label}</div>
      {children}
      {hint && <div className="tc3 fs10 mt-4">{hint}</div>}
    </div>
  );
}
function Inp({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      className="fi"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
    />
  );
}
function Sel({ value, onChange, options, disabled, multiple, size }) {
  return (
    <select
      className="fsel"
      value={multiple ? value || [] : value || ""}
      onChange={(e) => {
        if (multiple) {
          onChange(Array.from(e.target.selectedOptions).map((o) => o.value));
        } else {
          onChange(e.target.value);
        }
      }}
      disabled={disabled}
      multiple={multiple}
      size={multiple && size ? size : undefined}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="tsearch">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef(null),
    buttonRef = useRef(null),
    menuRef = useRef(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    const h = (e) => {
      if (!open) return;
      if (
        wrapperRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const handleToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 190;
      const left = Math.min(
        Math.max(rect.left, 8),
        window.innerWidth - menuWidth - 8,
      );
      setPos({ top: rect.bottom + 8, left });
    }
    setOpen((prev) => !prev);
  }, []);
  const menu = open ? (
    <div
      className="adrop"
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        right: "auto",
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => (
        <div
          key={i}
          className={`adrop-item${item.danger ? " red" : ""}`}
          onClick={() => {
            setOpen(false);
            item.onClick?.();
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  ) : null;
  return (
    <div className="action-wrap" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-g btn-sm"
        onClick={handleToggle}
      >
        •••
      </button>
      {mounted && menu && createPortal(menu, document.body)}
    </div>
  );
}
function Toast({ toasts }) {
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}
function ReviewBlock({ data }) {
  return (
    <div className="review-block">
      {Object.entries(data)
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k} className="review-row">
            <span className="review-label">{k}</span>
            <span className="review-value">{String(v)}</span>
          </div>
        ))}
    </div>
  );
}
function FormCard({ icon, label, children }) {
  return (
    <div className="panel mb-16">
      <div className="ph">
        {icon && <span className="fs-16">{icon}</span>}
        <span className="ph-title">{label}</span>
      </div>
      <div className="pb">{children}</div>
    </div>
  );
}
function FormFooter({ onCancel, onSubmit, loading, label = "Save", danger }) {
  return (
    <div className="fb gap-8 pt-16" style={{ marginBottom: 25 }}>
      <Btn
        variant="g"
        onClick={onCancel}
        disabled={loading}
        className="btn-solid-bw"
      >
        Cancel
      </Btn>
      <Btn variant={danger ? "d" : "p"} onClick={onSubmit} disabled={loading}>
        {loading ? <span className="spin-icon" /> : label}
      </Btn>
    </div>
  );
}
function PageHeader({ title, subtitle, subject, onBack }) {
  return <div className="mb-22"></div>;
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div
        className={`modal${wide ? " modal-lg" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhdr">
          <span className="mtitle">{title}</span>
          <Btn variant="g" sm onClick={onClose}>
            ✕
          </Btn>
        </div>
        <div className="mbody">{children}</div>
      </div>
    </div>
  );
}
function DataTable({ columns, rows, emptyMsg = "No data found" }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="empty"
                style={{ textAlign: "center", padding: "48px 0" }}
              >
                {emptyMsg}
              </td>
            </tr>
          ) : (
            rows
          )}
        </tbody>
      </table>
    </div>
  );
}
function UserPicker({ users, label = "Select User", value, onChange }) {
  const opts = [
    { value: "", label: `— ${label} —` },
    ...users
      .filter((u) => resolveUserId(u) != null)
      .map((u) => {
        const id = resolveUserId(u);
        return {
          value: String(id),
          label: `${u.name}${u.email ? ` (${u.email})` : ""}`,
        };
      }),
  ];
  return <Sel value={value} onChange={onChange} options={opts} />;
}

/* ═══════════════════════════════════════════════════════
   SHARED: ACCESS DENIED CARD
═══════════════════════════════════════════════════════ */
function AccessDeniedCard({ text, onBack }) {
  return (
    <div className="panel" style={{ padding: 28 }}>
      <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
      <h2 style={{ marginBottom: 6, fontSize: 16 }}>Access Denied</h2>
      <p className="tc3" style={{ fontSize: 13, marginBottom: 20 }}>
        {text || "You do not have permission to perform this action."}
      </p>
      <Btn variant="g" onClick={onBack}>
        ← Go Back
      </Btn>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ADD BRANCH PAGE
═══════════════════════════════════════════════════════ */
function AddBranchPage({ onSave, onBack }) {
  const { canCreateBranches } = usePermissions();
  const [d, setD] = useState({});
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locError, setLocError] = useState("");

  const upd = (k, v) => setD((x) => ({ ...x, [k]: v }));

  if (!canCreateBranches) {
    return (
      <div className="page action-page" style={{ maxWidth: 1600 }}>
        <AccessDeniedCard
          text="You do not have permission to create branches."
          onBack={onBack}
        />
      </div>
    );
  }

  async function submit() {
    if (!d.name?.trim()) {
      alert("Branch name is required.");
      return;
    }
    if (!location || (!location.confirmed && !location.addr1?.trim())) {
      setLocError("Please search for an address or drop a pin on the map.");
      return;
    }
    if (!location.unitNumber?.trim()) {
      setLocError("Unit number is required.");
      return;
    }
    setLocError("");
    setLoading(true);
    const mergedLine1 =
      location.mode === "pin"
        ? [location.unitNumber, location.label].filter(Boolean).join(", ")
        : [location.unitNumber, location.addr1].filter(Boolean).join(", ");
    const addressPayload =
      location.mode === "pin"
        ? {
            address_code: d.address_code || "",
            address_line_1: mergedLine1,
            address_line_2: "",
            city: "",
            region: d.region || "",
            postal_code: "",
            country: "",
            latitude: location.lat,
            longitude: location.lng,
          }
        : {
            address_code: d.address_code || "",
            address_line_1: mergedLine1,
            address_line_2: location.addr2 || "",
            city: location.city || "",
            region: location.region || d.region || "",
            postal_code: location.postal || "",
            country: location.country || "",
            latitude: location.lat || null,
            longitude: location.lng || null,
          };
    const payload = {
      branch_name: d.name || "",
      vat_registration_number: d.vat || "",
      company_registration_number: d.company || "",
      address: addressPayload,
    };
    const res = await API.createBranch(payload);
    onSave(res || { id: Date.now(), name: d.name });
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Add New Branch"
        subtitle="Register a new operational branch"
        onBack={onBack}
      />
      <FormCard icon="" label="Branch Information">
        <FG label="Branch Name">
          <Inp
            value={d.name}
            onChange={(v) => upd("name", v)}
            placeholder="e.g. Soweto Central"
          />
        </FG>
        <div className="g2">
          <FG label="VAT Registration Number">
            <Inp
              value={d.vat}
              onChange={(v) => upd("vat", v)}
              placeholder="VAT123456"
            />
          </FG>
        </div>
        <div className="g2">
          <FG label="Company Registration Number">
            <Inp
              value={d.company}
              onChange={(v) => upd("company", v)}
              placeholder="CRN123456"
            />
          </FG>
          <FG label="Address Code">
            <Inp
              value={d.address_code}
              onChange={(v) => upd("address_code", v)}
              placeholder="ADDR001"
            />
          </FG>
        </div>
      </FormCard>
      <FormCard icon="📍" label="Location & Address">
        {locError && (
          <div
            style={{
              padding: "8px 12px",
              marginBottom: 12,
              borderRadius: 8,
              background: "rgba(220,53,69,.08)",
              border: "0.5px solid rgba(220,53,69,.3)",
              color: "#e05260",
              fontSize: 12,
            }}
          >
            ⚠️ {locError}
          </div>
        )}
        <AddressMapPicker
          value={location || {}}
          onChange={(data) => {
            setLocation(data);
            if (data.region) upd("region", data.region);
            if (locError && (data.confirmed || data.addr1?.trim()))
              setLocError("");
          }}
          required
        />
        <div style={{ height: 12 }} />
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Create Branch"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   EDIT BRANCH PAGE
═══════════════════════════════════════════════════════ */
function EditBranchPage({ branch, onSave, onBack }) {
  const { canUpdateBranches } = usePermissions();
  const [d, setD] = useState({ ...branch });
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);

  const upd = (k, v) => setD((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    let mounted = true;
    API.getBranch(branch.id).then((res) => {
      if (!res || !mounted) return;
      const b = Array.isArray(res)
        ? res[0]
        : res.branch || res.data?.branch || res.data || res;
      setD((x) => ({
        ...x,
        name: b.branch_name || x.name,
        code: b.branch_code || x.code,
        address_code: b.address?.address_code || x.address_code,
        address_id: b.address?.address_id || x.address_id,
        region: b.region || b.address?.region || x.region,
        organization_name: b.organization?.name || x.organization_name,
        organization_id: b.organization?.organization_id || x.organization_id,
        status: b.is_active ? "Active" : "Inactive",
        vat: b.vat_registration_number || b.vat_registration || x.vat,
        company:
          b.company_registration_number || b.company_registration || x.company,
        addr1: b.address?.address_line_1 || x.addr1,
        addr2: b.address?.address_line_2 || x.addr2,
        city: b.address?.city || x.city,
        postal: b.address?.postal_code || x.postal,
        country: b.address?.country || x.country,
      }));
      const addr = b.address || {};
      const initialLoc = {
        confirmed: !!(addr.address_line_1 || addr.latitude || addr.longitude),
        mode: addr.latitude && addr.longitude ? "pin" : "search",
        lat: addr.latitude || null,
        lng: addr.longitude || null,
        addr1: addr.address_line_1 || "",
        addr2: addr.address_line_2 || "",
        city: addr.city || "",
        region: addr.region || "",
        postal: addr.postal_code || "",
        country: addr.country || "",
        label: addr.address_line_1 || "",
        unitNumber: "",
      };
      setLocation(initialLoc);
    });
    return () => (mounted = false);
  }, [branch.id]);

  if (!canUpdateBranches) {
    return (
      <div className="page action-page" style={{ maxWidth: 1600 }}>
        <AccessDeniedCard
          text="You do not have permission to edit branches."
          onBack={onBack}
        />
      </div>
    );
  }

  async function submit() {
    setLoading(true);
    const payload = {
      branch_id: branch.id,
      branch_name: d.name,
      // NOTE: backend expects numeric 0/1, not boolean true/false
      is_active: d.status === "Active" ? 1 : 0,
      vat_registration_number: d.vat || "",
      company_registration_number: d.company || "",
      address: {
        address_code: d.address_code || "",
        address_line_1: d.addr1 || "",
        address_line_2: d.addr2 || "",
        address_id: d.address_id || "",
        city: d.city || "",
        region: d.region || "",
        postal_code: d.postal || "",
        country: d.country || "",
      },
    };
    await API.updateBranch(branch.id, payload);
    onSave({ ...branch, ...d });
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Edit Branch"
        subtitle="Update branch details"
        subject={` ${branch.name}`}
        onBack={onBack}
      />
      <FormCard icon="" label="Branch Information">
        <FG label="Branch Name">
          <Inp value={d.name} onChange={(v) => upd("name", v)} />
        </FG>
        <div className="g2">
          <FG label="Branch Code">
            <Inp value={d.code} onChange={(v) => upd("code", v)} />
          </FG>
          <FG label="Address Code">
            <Inp
              value={d.address_code}
              onChange={(v) => upd("address_code", v)}
            />
          </FG>
        </div>
        <div className="g2">
          <FG label="Status">
            <Sel
              value={d.status}
              onChange={(v) => upd("status", v)}
              options={[
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
              ]}
            />
          </FG>
        </div>
        <div className="g2">
          <FG label="VAT Registration">
            <Inp value={d.vat} onChange={(v) => upd("vat", v)} />
          </FG>
          <FG label="Company Registration">
            <Inp value={d.company} onChange={(v) => upd("company", v)} />
          </FG>
        </div>
      </FormCard>
      <FormCard icon="📍" label="Location & Address">
        <AddressMapPicker
          value={location || {}}
          onChange={(data) => {
            setLocation(data);
            if (data.addr1 != null) upd("addr1", data.addr1);
            if (data.addr2 != null) upd("addr2", data.addr2);
            if (data.city != null) upd("city", data.city);
            if (data.postal != null) upd("postal", data.postal);
            if (data.country != null) upd("country", data.country);
            if (data.region) upd("region", data.region);
          }}
          required
        />
        <div style={{ height: 12 }} />
        <FG label="Street Address">
          <Inp value={d.addr1} onChange={(v) => upd("addr1", v)} />
        </FG>
        <FG label="Address Line 2">
          <Inp value={d.addr2} onChange={(v) => upd("addr2", v)} />
        </FG>
        <div className="g2">
          <FG label="City">
            <Inp value={d.city} onChange={(v) => upd("city", v)} />
          </FG>
          <FG label="Postal Code">
            <Inp value={d.postal} onChange={(v) => upd("postal", v)} />
          </FG>
        </div>
        <FG label="Country">
          <Inp value={d.country} onChange={(v) => upd("country", v)} />
        </FG>
        <div style={{ height: 8 }} />
        <FG label="Province">
          <Sel
            value={d.region || ""}
            onChange={(v) => upd("region", v)}
            options={SA_PROVINCES}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Save Changes"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   DELETE BRANCH PAGE
═══════════════════════════════════════════════════════ */
function DeleteBranchPage({ branch, onConfirm, onBack }) {
  const { canDeleteBranches } = usePermissions();
  const [loading, setLoading] = useState(false);

  if (!canDeleteBranches) {
    return (
      <div className="page action-page" style={{ maxWidth: 1600 }}>
        <AccessDeniedCard
          text="You do not have permission to delete branches."
          onBack={onBack}
        />
      </div>
    );
  }

  async function confirm() {
    setLoading(true);
    await API.deleteBranch(branch.id);
    onConfirm(branch.id);
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Delete Branch"
        subtitle="This will permanently remove the branch"
        subject={` ${branch.name}`}
        onBack={onBack}
      />
      <div className="danger-zone mb-16">
        <div className="danger-zone-title">⚠ Danger Zone</div>
        <div className="danger-zone-msg">
          Deleting this branch affects all associated warehouses and users.
          Cannot be undone.
        </div>
      </div>
      <FormCard label="Branch to be Deleted">
        <ReviewBlock
          data={{
            Name: branch.name,
            Code: branch.code,
            Region: branch.region,
            Organization: branch.organization || branch.organization_name,
            Status: branch.status,
          }}
        />
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={confirm}
        loading={loading}
        label="Confirm Delete"
        danger
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MODALS
═══════════════════════════════════════════════════════ */
function BindBranchUserModal({ branch, users, onClose, toast }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit() {
    if (!selectedUserId) {
      alert("Select a user.");
      return;
    }
    setLoading(true);
    const res = await API.bindBranch(branch.id, resolveUserId(selectedUserId));
    if (res) {
      toast(`User bound to ${branch.name}`, "success");
      onClose();
    } else alert("Failed to bind user.");
    setLoading(false);
  }
  return (
    <Modal title={`Bind User → ${branch.name}`} onClose={onClose}>
      <FG label="Select User">
        <UserPicker
          users={users}
          label="Select user"
          value={selectedUserId}
          onChange={setSelectedUserId}
        />
      </FG>
      <div className="fb gap-8 pt-16">
        <Btn variant="g" onClick={onClose}>
          Cancel
        </Btn>
        <Btn variant="p" onClick={submit} disabled={loading}>
          {loading ? <span className="spin-icon" /> : "Bind User"}
        </Btn>
      </div>
    </Modal>
  );
}

function UnbindBranchUserModal({ branch, users, onClose, toast }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit() {
    if (!selectedUserId) {
      alert("Select a user.");
      return;
    }
    setLoading(true);
    const res = await API.unbindBranch(
      branch.id,
      resolveUserId(selectedUserId),
    );
    if (res) {
      toast(`User unbound from ${branch.name}`, "success");
      onClose();
    } else alert("Failed to unbind user.");
    setLoading(false);
  }
  return (
    <Modal title={`Unbind User ← ${branch.name}`} onClose={onClose}>
      <FG label="Select User">
        <UserPicker
          users={users}
          label="Select user"
          value={selectedUserId}
          onChange={setSelectedUserId}
        />
      </FG>
      <div className="fb gap-8 pt-16">
        <Btn variant="g" onClick={onClose}>
          Cancel
        </Btn>
        <Btn variant="d" onClick={submit} disabled={loading}>
          {loading ? <span className="spin-icon" /> : "Unbind User"}
        </Btn>
      </div>
    </Modal>
  );
}

function BranchCustomersModal({ branch, onClose }) {
  const [arr, setArr] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    API.getCustomerBranches(branch.id).then((res) => {
      setArr(toArray(res, "customers", "branches", "data", "items"));
      setLoading(false);
    });
  }, [branch.id]);
  return (
    <Modal title={`Customer Branches — ${branch.name}`} onClose={onClose} wide>
      {loading ? (
        <div className="empty" style={{ textAlign: "center", minHeight: 60 }}>
          Loading…
        </div>
      ) : arr.length === 0 ? (
        <div className="empty" style={{ textAlign: "center", minHeight: 60 }}>
          No customer branches linked.
        </div>
      ) : (
        <DataTable
          columns={["Branch Name", "Code", "Region", "Status"]}
          rows={arr.map((b, i) => (
            <tr key={i}>
              <td className="fw-6">{b.branch_name || b.name || "-"}</td>
              <td className="mono">{b.branch_code || b.code || "-"}</td>
              <td>{b.region || b.address?.region || "-"}</td>
              <td>
                <Badge color={b.is_active ? "green" : "red"}>
                  {b.is_active ? "Active" : "Inactive"}
                </Badge>
              </td>
            </tr>
          ))}
        />
      )}
    </Modal>
  );
}

function BranchSuppliersModal({ branch, onClose }) {
  const [arr, setArr] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    API.getSupplierBranches(branch.id).then((res) => {
      setArr(toArray(res, "branches", "data", "items"));
      setLoading(false);
    });
  }, [branch.id]);
  return (
    <Modal title={`Supplier Branches — ${branch.name}`} onClose={onClose} wide>
      {loading ? (
        <div className="empty" style={{ textAlign: "center", minHeight: 60 }}>
          Loading…
        </div>
      ) : arr.length === 0 ? (
        <div className="empty" style={{ textAlign: "center", minHeight: 60 }}>
          No supplier branches linked.
        </div>
      ) : (
        <DataTable
          columns={["Branch Name", "Region", "Status"]}
          rows={arr.map((b, i) => (
            <tr key={i}>
              <td className="fw-6">{b.branch_name || b.name || "-"}</td>
              <td>{b.region || b.address?.region || "-"}</td>
              <td>
                <Badge color={b.is_active ? "green" : "red"}>
                  {b.is_active ? "Active" : "Inactive"}
                </Badge>
              </td>
            </tr>
          ))}
        />
      )}
    </Modal>
  );
}

function BranchesByUserModal({ users, onClose }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [arr, setArr] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  async function lookup() {
    if (!selectedUserId) return;
    setLoading(true);
    const res = await API.getBranchesByUser(resolveUserId(selectedUserId));
    setArr(toArray(res, "branches", "data", "items"));
    setFetched(true);
    setLoading(false);
  }
  return (
    <Modal title="Branch Lookup by User" onClose={onClose} wide>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <UserPicker
            users={users}
            label="Select user to look up"
            value={selectedUserId}
            onChange={setSelectedUserId}
          />
        </div>
        <Btn variant="p" onClick={lookup} disabled={loading || !selectedUserId}>
          {loading ? <span className="spin-icon" /> : "Look Up"}
        </Btn>
      </div>
      {!fetched ? (
        <div
          className="empty tc3 fs12"
          style={{ textAlign: "center", minHeight: 60 }}
        >
          Select a user and click Look Up to see their branch assignments.
        </div>
      ) : arr.length === 0 ? (
        <div className="empty" style={{ textAlign: "center", minHeight: 60 }}>
          No branches found for this user.
        </div>
      ) : (
        <DataTable
          columns={["Branch Name", "Region", "Status"]}
          rows={arr.map((b, i) => (
            <tr key={i}>
              <td className="fw-6">{b.branch_name || b.name || "-"}</td>
              <td>{b.region || b.address?.region || "-"}</td>
              <td>
                <Badge color={b.is_active ? "green" : "red"}>
                  {b.is_active ? "Active" : "Inactive"}
                </Badge>
              </td>
            </tr>
          ))}
        />
      )}
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════
   BRANCHES LIST
═══════════════════════════════════════════════════════ */
function BranchesList({ branches, users, onNavigate, toast }) {
  const { canCreateBranches, canUpdateBranches, canDeleteBranches } =
    usePermissions();

  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);

  const filtered = branches.filter((b) =>
    `${b.name} ${b.region} ${b.city} ${b.organization || b.organization_name}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <>
      <div className="panel">
        <div className="tbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search branches…"
          />
          <div className="flex-1" />
          <Btn
            variant="g"
            sm
            onClick={() => setModal({ type: "branchesByUser" })}
          >
            By User
          </Btn>
          {canCreateBranches && (
            <Btn variant="p" sm onClick={() => onNavigate("addBranch")}>
              ＋ Add Branch
            </Btn>
          )}
        </div>

        <DataTable
          columns={[
            "Branch Name",
            "Code",
            "Region",
            "Organization",
            "City",
            "Status",
            "Actions",
          ]}
          rows={filtered.map((b) => {
            const menuItems = [
              {
                label: " Suppliers",
                onClick: () => setModal({ type: "branchSuppliers", data: b }),
              },
              canUpdateBranches && {
                label: " Bind User",
                onClick: () => setModal({ type: "bindUser", data: b }),
              },
              canUpdateBranches && {
                label: " Unbind User",
                onClick: () => setModal({ type: "unbindUser", data: b }),
              },
              canDeleteBranches && {
                label: "Delete",
                onClick: () => onNavigate("deleteBranch", b),
                danger: true,
              },
            ].filter(Boolean);

            return (
              <tr key={b.id}>
                <td className="fw-6">{b.name}</td>
                <td className="mono">{b.code || "-"}</td>
                <td>{b.region || "-"}</td>
                <td>{b.organization || b.organization_name || "-"}</td>
                <td>{b.city || "-"}</td>
                <td>
                  <Badge color={b.status === "Active" ? "green" : "amber"}>
                    {b.status}
                  </Badge>
                </td>
                <td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {canUpdateBranches && (
                      <Btn
                        variant="s"
                        sm
                        onClick={() => onNavigate("editBranch", b)}
                      >
                        Edit
                      </Btn>
                    )}
                    {menuItems.length > 0 && <ActionMenu items={menuItems} />}
                  </div>
                </td>
              </tr>
            );
          })}
          emptyMsg="No branches found"
        />
      </div>

      {modal?.type === "branchesByUser" && (
        <BranchesByUserModal users={users} onClose={() => setModal(null)} />
      )}
      {modal?.type === "branchCustomers" && (
        <BranchCustomersModal
          branch={modal.data}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "branchSuppliers" && (
        <BranchSuppliersModal
          branch={modal.data}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "bindUser" && (
        <BindBranchUserModal
          branch={modal.data}
          users={users}
          onClose={() => setModal(null)}
          toast={toast}
        />
      )}
      {modal?.type === "unbindUser" && (
        <UnbindBranchUserModal
          branch={modal.data}
          users={users}
          onClose={() => setModal(null)}
          toast={toast}
        />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   ROOT — BranchesPage
═══════════════════════════════════════════════════════ */
export default function BranchesPage({ showToast: externalToast }) {
  const {
    canSeePage,
    canCreateBranches,
    canUpdateBranches,
    canDeleteBranches,
    ready,
  } = usePermissions();

  const [page, setPage] = useState(null);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);

  function toast(msg, type = "info") {
    if (externalToast) externalToast(msg, type);
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [b, u] = await Promise.all([API.getBranches(), API.getUsers()]);
    setBranches(b || []);
    setUsers(u || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  function navigate(view, data = null) {
    if (view === "addBranch" && !canCreateBranches) return;
    if (view === "editBranch" && !canUpdateBranches) return;
    if (view === "deleteBranch" && !canDeleteBranches) return;
    setPage({ view, data });
  }

  function goBack() {
    setPage(null);
  }

  if (ready && !canSeePage("Branch")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">You do not have permission to view branches.</p>
      </div>
    );
  }

  if (page) {
    const { view, data } = page;
    if (view === "addBranch")
      return (
        <AddBranchPage
          onSave={async () => {
            toast("Branch created", "success");
            goBack();
            const fresh = await API.getBranches();
            setBranches(fresh || []);
          }}
          onBack={goBack}
        />
      );
    if (view === "editBranch")
      return (
        <EditBranchPage
          branch={data}
          onSave={async () => {
            toast("Branch updated", "success");
            goBack();
            const fresh = await API.getBranches();
            setBranches(fresh || []);
          }}
          onBack={goBack}
        />
      );
    if (view === "deleteBranch")
      return (
        <DeleteBranchPage
          branch={data}
          onConfirm={async () => {
            toast("Branch deleted", "warning");
            goBack();
            const fresh = await API.getBranches();
            setBranches(fresh || []);
          }}
          onBack={goBack}
        />
      );
  }

  return (
    <div className="page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div></div>
      </div>
      {loading ? (
        <div
          className="empty"
          style={{
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            minHeight: 200,
          }}
        >
          Loading branches…
        </div>
      ) : (
        <BranchesList
          branches={branches}
          users={users}
          onNavigate={navigate}
          toast={toast}
        />
      )}
      <Toast toasts={toasts} />
    </div>
  );
}
